import React from 'react';
import { withRouter } from 'react-router';

import Box from '@material-ui/core/Box';
import DialogContent from '@material-ui/core/DialogContent';
import {
  TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL,
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL
} from 'config/urls';
import { FieldArray, Formik } from 'formik';
import {
  get,
  isArray,
  isEmpty,
  isNil,
  isNull,
  map,
  omit,
  some,
  toInteger
} from 'lodash';
import moment from 'moment';
import { bulkUploadAssignmentAssign } from 'sdk';

import Stepper from 'pages/Teachers/shared/AssignmentDialog/components/Stepper';
import { LIVE_CLASSROOM_TEACHERS_DOMAIN } from 'utils/constants';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors } from 'utils/notifications';
import { reverse } from 'utils/urls';
import { parseParams } from 'utils/urls';

import StepperAssignmentAssignForm from './components/StepperAssignmentAssignForm';
import UserContext from 'components/UserContext';

import { allSectionsList, assignmentAssign, recentSectionsList } from './sdk';
import styles from './styles.module.scss';
import { assignValidationSchema } from './utils';

const LIVE_CLASSROOM_ENABLED =
  process.env.REACT_APP_LIVE_CLASSROOM_ENABLED === 'true';

class AssignAssignment extends React.Component {
  static contextType = UserContext;

  state = {
    assigned: false,
    recentSections: [],
    allSections: [],
    loading: true
  };

  componentDidMount() {
    // Left like this in order to avoid refactoring in a PR that does another thing.
    this.trackerId = this.props.trackerId;
    this.init();
  }

  async init() {
    await Promise.all([this.fetchRecentSections(), this.fetchAllSections()]);
  }

  fetchRecentSections = async () => {
    const { data, success, errors } = await recentSectionsList();

    if (success) {
      this.setState({ recentSections: data });
    } else {
      notifyErrors(errors);
    }
  };

  fetchAllSections = async () => {
    const { data, success, errors } = await allSectionsList();

    if (success) {
      this.setState({ allSections: data, loading: false });
    } else {
      this.setState({ loading: false });
      notifyErrors(errors);
    }
  };

  onSubmit = async (values, actions) => {
    const { success, errors } = await assignmentAssign(this.trackerId, {
      sections_data: map(values.sections, (sectionData) => ({
        section_id: sectionData.sectionId,
        ...omit(sectionData, 'sectionId')
      }))
    });

    if (success) {
      const { bulkUploadId, user } = this.props;

      trackMixpanelEvent(
        user.id,
        `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
        {
          feature: 'create',
          view: 'assign',
          form: 'assign',
          classes: values?.sections?.length
        }
      );

      if (!isNil(bulkUploadId)) {
        const { success, errors } = await bulkUploadAssignmentAssign({
          bulkUploadId,
          data: { assignment: this.trackerId }
        });

        if (success) {
          this.props.history.push(
            reverse(TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL, {
              bulkUploadId
            })
          );

          this.props.onDialogClose();
        } else {
          notifyErrors(errors);
        }
      } else {
        if (
          (user?.is_live_classroom_teacher ||
            LIVE_CLASSROOM_TEACHERS_DOMAIN.includes(
              user.email.split('@')[1]
            )) &&
          LIVE_CLASSROOM_ENABLED
        ) {
          this.props.onDialogClose();
          window.location.reload();
        } else {
          this.props.history.push(
            reverse(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
          );
        }

        this.props.onDialogClose();
      }
    } else {
      notifyErrors(errors);
    }
  };

  defaultOpenDate = () => moment().format();

  defaultDueDate = () => {
    // The default due datetime wanted by specification is two days after today at 3pm.
    let m = moment().add(2, 'days');
    m.set({ h: 15, m: 0 });
    return m.format();
  };

  buildDefaultSection = (sectionId) => ({
    sectionId,
    open: this.defaultOpenDate(),
    due: this.defaultDueDate()
  });

  buildInitialValues = () => {
    if (this.state.loading) {
      return {};
    }

    const { selectedSections } = this.props;

    // If there are sections in the selectedSections prop(used when a user goes back so that we save the state) we set them in the initial values.
    if (!isNull(selectedSections)) {
      return { sections: selectedSections };
    }

    // If there're sections in the URL params(and there aren't any other selected sections) we set them in the initial values.
    const searchParams = parseParams(this.props.history.location.search);
    let initialSelectedSectionIds = get(
      searchParams,
      'initialSelectedSectionIds',
      []
    );

    if (!isArray(initialSelectedSectionIds)) {
      // If it's just a single value - make it an array so we can map over it.
      initialSelectedSectionIds = [initialSelectedSectionIds];
    }

    // Map over the initial selected section ids. If none was set we'll basically be returning an empty array(which is the initial value we're aiming for).
    const initialSections = initialSelectedSectionIds.map((sectionId) =>
      this.buildDefaultSection(toInteger(sectionId))
    );

    return { sections: initialSections };
  };

  goBack = (sectionsData) => {
    this.props.storeSelectedSections(sectionsData);
    this.props.prevStep();
  };

  render() {
    const { assigned, recentSections, allSections, loading } = this.state;

    return (
      <Formik
        enableReinitialize
        validationSchema={assignValidationSchema}
        initialValues={this.buildInitialValues()}
        onSubmit={this.onSubmit}
      >
        {({ errors, isSubmitting, handleSubmit, values }) => {
          const disableSubmit =
            isSubmitting ||
            !isEmpty(errors) ||
            loading ||
            // There is a racing condition between the initial values of the form and the yup validation.
            // We make sure the Assign button is disabled if this happens.
            some(values.sections, { sectionId: '' });

          if (!assigned) {
            return (
              <>
                <div className={styles.stepperContainer}>
                  <Stepper
                    nextButtonText={'Assign →'}
                    backButtonText={'← Back'}
                    onNextClick={handleSubmit}
                    activeTab={'assign'}
                    disableNextButton={disableSubmit}
                    onBackClick={() => this.goBack(values.sections)}
                    disableConfirm={this.props.disableConfirmStep}
                    hideBackButton={this.props.hideBackButton}
                  />
                </div>

                <DialogContent className={styles.dialogContent}>
                  <Box>
                    <FieldArray
                      name="sections"
                      component={(props) => (
                        <StepperAssignmentAssignForm
                          {...props}
                          recentSections={recentSections}
                          allSections={allSections}
                          buildDefaultSection={this.buildDefaultSection}
                        />
                      )}
                    />
                  </Box>
                </DialogContent>
              </>
            );
          }
        }}
      </Formik>
    );
  }
}

export default withRouter(AssignAssignment);
